import { put, delay, takeEvery, all, call, select, race, cancelled } from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import {
  setPageTopLoader,
  setHomePictreeCheckInCommon,
  setHomePictreeCheckInOverviewCommon,
  setPictreeDataCheckInTrendCommon,
  setHomePictreePhotoShootCommon,
  setPictreeDataPhotoShootTrendCommon,
  setPictreePhotoShootByAssetCommon,
  setPictreePhotoShootTrendByAssetCommon,
  setPictreeCheckInByAssetCommon,
  setPictreeCheckInTrendByAssetCommon,
  patchHomePictreePhotoShootCommon,
  setByAreaDataSummaryCommon,
  setByAreaDataTableCommon,
  setByAreaDataCommon,
  setByAreaCheckInDataCommon,
} from "actions";
import { getJwtToken, getDataFromRDS, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";
import assetConfig from "../../utils/pictree/AssetConfig";
import chartConfig from "../../utils/ChartConfig";

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000,   // 30s
  retries: 3,         // 合計 4 回までトライ（初回 + リトライ3）
  backoffBase: 1500,  // 1.5s指数バックオフ
};

const USERS_LIGHT_RETRY = {
  timeoutMs: 15000,   // 15s
  retries: 1,
};

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,           // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2,                 // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000,          // 1秒
    backoffFactor = 2,           // 2倍ずつ
    jitter = true,               // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || '';
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    }
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功

    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
        // もし fetch/axios なら AbortController を仕込むとさらに良い
      }
    }
  }
}

function* runEffectsInBatches(effects, { concurrency = 2, gapMs = 300 } = {}) {
  const results = [];
  for (let i = 0; i < effects.length; i += concurrency) {
    const batch = effects.slice(i, i + concurrency);
    // 同時に投げるのは batch サイズ分だけ
    const batchResults = yield all(batch);
    results.push(...batchResults);

    // 次バッチの前にクールダウン（最後のバッチは不要）
    if (i + concurrency < effects.length && gapMs > 0) {
      yield delay(gapMs);
    }
  }
  return results;
}

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTreeCommon;
export const selectedSeasonCommon = (state) => state.pictreeCommon.selectedSeason;

const REPORT_SEASON_ID = "cmt8v3v940001a4zc1b2v7nvx";

function getCommonDataRangeUtc(rangeTarget, selectedSeasonData) {
  const requestedStart = moment.tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo");
  const reportSeasonSelected = selectedSeasonData.some(
    season => season.seasonId === REPORT_SEASON_ID
  );
  const seasonStart = moment.tz("2026-09-01 06:00:00", "Asia/Tokyo");
  const start = reportSeasonSelected && requestedStart.isBefore(seasonStart)
    ? seasonStart
    : requestedStart;
  const end = moment.tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo").add(1, "day");

  return {
    startUtc: start.utc().format("YYYY-MM-DD HH:mm:ss"),
    endUtc: end.utc().format("YYYY-MM-DD HH:mm:ss"),
  };
}

function splitDateRangeByWeek(startDate, endDate) {
  const start = moment(startDate);
  const end = moment(endDate);
  const result = [];

  let current = start.clone();
  while (current.isBefore(end)) {
    const weekStart = current.clone();
    const weekEnd = moment.min(weekStart.clone().endOf("week"), end);
    result.push({
      weekStart: weekStart.format("YYYY-MM-DD"),
      weekEnd: weekEnd.format("YYYY-MM-DD"),
    });
    current = weekEnd.clone().add(1, "day");
  }
  return result;
}

// ===== クエリビルダ（承認イベント基準 / pad.type=NTT は動的）=====
const createApprovedBySubtypeQuery = ({ startUtc, endUtc, seasonId, needPadNtt }) => {
  // pad JOIN / 条件は Hokuden と共用シーズンだけ付与
  const padJoin  = needPadNtt ? `JOIN power_asset_detail pad ON rr.power_asset_id = pad.power_asset_id` : ``;
  const padWhere = needPadNtt ? `AND pad.type = 'NTT_FT'` : ``;

  return {
    query : `
      SELECT pa.type, pa.sub_type, COUNT(DISTINCT rr.power_asset_id) AS reviewOkcnt
      FROM review_request_event rre
      JOIN review_request rr ON rre.review_request_id = rr.id
      ${padJoin}
      JOIN power_asset pa ON pa.id = rr.power_asset_id
      WHERE rre.game_space_id = '${seasonId}'
      AND rre.type = 'APPROVED'
      AND rre.occurred_datetime >= '${startUtc}'
      AND rre.occurred_datetime <  '${endUtc}'
      ${padWhere}
      GROUP BY pa.type, pa.sub_type
      ORDER BY reviewOkcnt DESC;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

export function* getPictreeDataHomePhotoShootCommon(action) {
  try {
    yield put(setPageTopLoader(true));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const seasonSelectedCommon = yield select(selectedSeasonCommon);

    if (!seasonSelectedCommon?.length) {
      // 初期化のみ
      yield put(setHomePictreePhotoShootCommon({
        countDenchu: 'loading',
        countUniqueUser: 'loading',
        countNewUser: undefined,
        countPic: 'loading',
        countAsset: 'loading',
        countDupeDenchu: 'loading',
        countReviewOk: 'loading',
        progressDenchu: { taken: 0, max: 0, maxByasset: 0 },
        timings: {}, errors: {},
      }));
      yield put(setPageTopLoader(false));
      return;
    }

    const seasonQuery = (() => {
      if (
        seasonSelectedCommon.length > 0 &&
        seasonSelectedCommon[0].seasonId !== undefined
      ) {
        const ids = seasonSelectedCommon
          .map((s) => `'${s.seasonId}'`)
          .join(", ");
        return seasonSelectedCommon.length === 1
          ? `AND gameSpaceId = ${ids}`         // ids は "'id1'"
          : `AND (gameSpaceId IN (${ids}))`;     // ids は "'id1','id2',…"
      }
      return "";
    })();

    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, seasonSelectedCommon);

    // 初回ロード（全部loading）
    yield put(setHomePictreePhotoShootCommon({
      countDenchu: 'loading',
      countUniqueUser: 'loading',
      countNewUser: undefined,
      countPic: 'loading',
      countAsset: 'loading',
      countDupeDenchu: 'loading',
      countReviewOk: 'loading',
      progressDenchu: { taken: 0, max: 0, maxByasset: 0 },
      timings: {}, errors: {},
    }));

    // max の事前設定（固定値）
    let maxNumber = 0;
    switch (seasonSelectedCommon[0].seasonId) {
      case "cmt8v3v940001a4zc1b2v7nvx": maxNumber = 125601;  break;
      default: break;
    }

    // 避難訓練は電柱撮影系ではなく、report / report_photograph 系に保存される。
    // 既存シーズンの集計条件には影響させず、このシーズンだけ専用集計を行う。
    if (seasonSelectedCommon[0].seasonId === "cmt8v3v940001a4zc1b2v7nvx") {
      const sqlQueryHinanKunrenSummary = {
        query: `
          SELECT
            COUNT(DISTINCT rp.id) AS total_photo_count,
            COUNT(DISTINCT CASE
              WHEN rp.id IS NOT NULL THEN r.id
            END) AS report_with_photo_count,
            COUNT(DISTINCT CASE
              WHEN rp.id IS NULL THEN r.id
            END) AS report_without_photo_count,
            COUNT(DISTINCT CASE
              WHEN r.status = 'APPROVED' THEN r.power_asset_id
            END) AS review_ok_asset_count,
            COUNT(DISTINCT CASE
              WHEN r.status = 'APPROVED' THEN r.player_id
            END) AS unique_photographers
          FROM report r
          LEFT JOIN report_photograph_bind rpb
            ON rpb.report_id = r.id
          LEFT JOIN report_photograph rp
            ON rp.id = rpb.report_photograph_id
          WHERE r.game_space_id = 'cmt8v3v940001a4zc1b2v7nvx'
            AND r.report_datetime >= '${startUtc}'
            AND r.report_datetime <  '${endUtc}';
        `,
        database: "KpiDashboard",
        type: "list",
      };

      const [hinanSummary] = yield call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [sqlQueryHinanKunrenSummary, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      );

      const summary = hinanSummary || {};
      const totalPhotoCount = Number(summary.total_photo_count) || 0;
      const reportWithPhotoCount = Number(summary.report_with_photo_count) || 0;
      const reportWithoutPhotoCount = Number(summary.report_without_photo_count) || 0;
      const reviewOkAssetCount = Number(summary.review_ok_asset_count) || 0;
      const uniquePhotographers = Number(summary.unique_photographers) || 0;

      yield put(setHomePictreePhotoShootCommon({
        countPic: totalPhotoCount,
        countReportWithPhoto: reportWithPhotoCount,
        countReportWithoutPhoto: reportWithoutPhotoCount,
        countDenchu: reviewOkAssetCount,
        countReviewOk: reviewOkAssetCount,
        countUniqueUser: uniquePhotographers,
        countNewUser: undefined,
        countDupeDenchu: 0,
        progressDenchu: { taken: reviewOkAssetCount, max: maxNumber, maxByasset: maxNumber },
      }));
      yield put(setPageTopLoader(false));
      return;
    }

    const sqlQueryCountPhotoDenchu = {
      "query": `
        SELECT COUNT(DISTINCT pae.power_asset_id) AS total_photo_power_assets
        FROM power_asset_event pae
        WHERE pae.game_space_id = '${seasonSelectedCommon[0].seasonId}'
          AND pae.occurred_datetime >= '${startUtc}'
          AND pae.occurred_datetime <  '${endUtc}';
      `,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountUniqueUser = {
      "query": `
        SELECT COUNT(DISTINCT rr.player_id) AS unique_photographers
        FROM review_request_event rre
        JOIN review_request rr ON rr.id = rre.review_request_id
        JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
        WHERE rr.game_space_id = '${seasonSelectedCommon[0].seasonId}'
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime < '${endUtc}';
      `,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountPicCount = {
      "query": `
        SELECT
          SUM(picCount) AS total_pic_count
        FROM (
          SELECT picCount FROM DenchuPhotoShootDenchuEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
          BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
          AND property IN ('nttft') ${seasonQuery}
        ) AS combined_data;`,
      "database": "KpiDashboard",
      "type": "list"
    };

    // dupe photoshoot - denchu
    const sqlQueryCountPhotoDupeDenchu = {
      "query": `
        SELECT COUNT(*) AS dupe_asset_count
        FROM (
          SELECT pae.power_asset_id
          FROM power_asset_event AS pae
          WHERE pae.game_space_id = '${seasonSelectedCommon[0].seasonId}'
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime <  '${endUtc}'
          GROUP BY pae.power_asset_id
          HAVING COUNT(*) > 1
        ) AS t;
      `,
      "database": "KpiDashboard",
      "type": "list"
    };

    const sqlQueryCountNewUsers = {
      query: `
        SELECT COUNT(*) AS first_time_photographers
        FROM (
          SELECT DISTINCT rr.player_id
          FROM review_request_event rre
          JOIN review_request rr ON rr.id = rre.review_request_id
          JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
          JOIN review_request_photo_bind bind ON bind.review_request_id = rr.id
          WHERE rr.game_space_id = '${seasonSelectedCommon[0].seasonId}'
            AND rre.type = 'APPROVED'
            AND rre.occurred_datetime BETWEEN
                  CONVERT_TZ('${rangeTarget[0]} 00:00:00','Asia/Tokyo','UTC')
              AND CONVERT_TZ('${rangeTarget[1]} 23:59:59','Asia/Tokyo','UTC')
        ) AS c
        WHERE NOT EXISTS (
          SELECT 1
          FROM review_request_event pre
          JOIN review_request rr_pre ON rr_pre.id = pre.review_request_id
          WHERE rr_pre.player_id = c.player_id
            AND pre.type = 'APPROVED'
            AND pre.occurred_datetime <
                  CONVERT_TZ('${rangeTarget[0]} 00:00:00','Asia/Tokyo','UTC')
        );
      `,
      database: 'KpiDashboard',
      type: 'list'
    };

    const sqlQueryCountReviewOk = {
      "query": `
        SELECT COUNT(DISTINCT rr.power_asset_id) AS reviewOk_count
        FROM review_request_event rre
        JOIN review_request rr ON rre.review_request_id = rr.id
        WHERE rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime <  '${endUtc}'
        AND rre.game_space_id = '${seasonSelectedCommon[0].seasonId}';
      `,
      "database": "KpiDashboard",
      "type": "list"
    };

    // ========= 1) 優先バッチ =========
    const [
      dataCountReviewOk
    ] = yield all([
      call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [sqlQueryCountReviewOk, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);

    // 先に描画
    const countReviewOk = dataCountReviewOk[0]["reviewOk_count"];

    yield put(setHomePictreePhotoShootCommon({
      countDenchu: 'loading',
      countAsset : 'loading',
      countReviewOk,
      countUniqueUser: 'loading',
      countNewUser: undefined,
      countPic: 'loading',
      countDupeDenchu: 'loading',
      progressDenchu: { taken: countReviewOk, max: maxNumber, maxByasset: maxNumber },
      timings: {}, errors: {},
    }));

    yield put(patchHomePictreePhotoShootCommon({
      //countPic: countPicCountValue == null ? 0 : parseInt(countPicCountValue, 10),
      progressDenchu: { taken: countReviewOk, max: maxNumber, maxByasset: maxNumber },
    }));

    // ========= 2) 次バッチ（並列2本） =========
    const [
      dataCountUniue,
      dataPhotoDenchu,
    ] = yield all([
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountUniqueUser, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountPhotoDenchu, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);
    const countPhotoDenchuValue = dataPhotoDenchu[0]["total_photo_power_assets"];
    const countUniqueUserValue = dataCountUniue[0]["unique_photographers"];
    yield put(patchHomePictreePhotoShootCommon({
      countUniqueUser: countUniqueUserValue,
      countDenchu: countPhotoDenchuValue,
      countAsset: countPhotoDenchuValue,
    }));

    const dataPicCountResp = yield call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [sqlQueryCountPicCount, ENDPOINT.GET_DATA_USERS, jwtToken],
      USERS_LIGHT_RETRY
    );
    const picStr = JSON.parse(dataPicCountResp.body)?.[0]?.[0]?.stringValue;
    const countPicNum = picStr == null ? 0 : parseInt(picStr, 10, 10);
    yield put(patchHomePictreePhotoShootCommon({ countPic: countPicNum }));

    // ========= 3) 最後のバッチ（重複：1本だけ）=========
    const dataDupePhotoDenchu = yield call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [sqlQueryCountPhotoDupeDenchu, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );
    const countDupeDenchuCountValue = dataDupePhotoDenchu[0]["dupe_asset_count"];
    yield put(patchHomePictreePhotoShootCommon({ countDupeDenchu: countDupeDenchuCountValue }));

    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export function* getPictreeDataHomePhotoShootTrendCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const selectedSeasonData = yield select(selectedSeasonCommon);


    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export function* getPictreeDataHomeCheckInCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const selectedSeasonData = yield select(selectedSeasonCommon);

    if (!rangeTarget || rangeTarget.length !== 2) {
      console.error("Invalid rangeTarget");
      yield put(setPageTopLoader(false));
      return;
    }

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }
    const seasonIdList = selectedSeasonData.map(season => `'${season.seasonId}'`).join(", ");
    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, selectedSeasonData);

    const sqlQueryCheckinSummary = {
      query: `
        SELECT COUNT(*) AS checkin_count,
        COUNT(DISTINCT pae.power_asset_id) AS checkin_asset_count,
        COUNT(DISTINCT pae.player_id) AS unique_user_count
        FROM power_asset_event pae
        WHERE pae.occurred_datetime >= '${startUtc}'
        AND pae.occurred_datetime < '${endUtc}'
        AND pae.type = 'CHECK_IN'
        AND pae.game_space_id IN (${seasonIdList})
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const [summaryTemp, mostAsset, mostUser] = yield all([
      call(
        getDataFromSxi,
        sqlQueryCheckinSummary,
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      ),
      call(
        getDataFromSxi,
        {
          query: `
            SELECT pae.power_asset_id AS power_asset_id, COUNT(*) AS checkin_count
            FROM power_asset_event pae
            WHERE pae.occurred_datetime >= '${startUtc}'
              AND pae.occurred_datetime < '${endUtc}'
              AND pae.type = 'CHECK_IN'
              AND pae.game_space_id IN (${seasonIdList})
            GROUP BY pae.power_asset_id
            ORDER BY checkin_count DESC
            LIMIT 1;
          `,
          database: "KpiDashboard",
          type: "list",
        },
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      ),
      call(
        getDataFromSxi,
        {
          query: `
            SELECT pae.player_id, MAX(ps.name) AS player_name, COUNT(*) AS checkin_count
            FROM power_asset_event pae
            LEFT JOIN player_state ps ON pae.player_id = ps.player_id
            WHERE pae.occurred_datetime >= '${startUtc}'
              AND pae.occurred_datetime < '${endUtc}'
              AND pae.type = 'CHECK_IN'
              AND pae.game_space_id IN (${seasonIdList})
            GROUP BY pae.player_id
            ORDER BY checkin_count DESC
            LIMIT 1;
          `,
          database: "KpiDashboard",
          type: "list",
        },
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      ),
    ]);

    const summary = summaryTemp?.[0] || {};
    const homeCheckInData = {
        checkInCount: Number(summary.checkin_count) || 0,
        checkInCountKisu: Number(summary.checkin_asset_count) || 0,
        checkInCountUniqueUser: Number(summary.unique_user_count) || 0,
        checkInMost: mostAsset?.[0]?.power_asset_id || "-",
        checkInMostUser: mostUser?.[0]?.player_name || "-",
        checkInRatio: "--", //checkInRatio,
      };
    yield put(setHomePictreeCheckInCommon(homeCheckInData));
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
    yield put(setHomePictreeCheckInCommon({}));
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataHomeCheckInOverviewCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const selectedSeasonData = yield select(selectedSeasonCommon);
    const selectedSeason = selectedSeasonData?.[0];

    if (!rangeTarget || rangeTarget.length !== 2 || !selectedSeason?.seasonId) {
      yield put(setHomePictreeCheckInOverviewCommon({
        status: "empty",
        error: null,
        checkinCount: undefined,
        uniqueCheckinAssetCount: undefined,
        uniqueCheckinUserCount: undefined,
        registeredAssetCount: undefined,
        achievementRate: undefined,
      }));
      return;
    }

    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, selectedSeasonData);
    const sqlQuery = {
      query: `
        WITH params AS (
          SELECT
            '${selectedSeason.seasonId}' AS game_space_id,
            '${startUtc}' AS start_datetime_utc,
            '${endUtc}' AS end_datetime_utc
        ),
        registered_assets AS (
          SELECT
            COUNT(DISTINCT paga.power_asset_id) AS registered_asset_count
          FROM power_asset_game_space_area paga
          JOIN params p
            ON p.game_space_id = paga.game_space_id
        ),
        checkin_summary AS (
          SELECT
            COUNT(*) AS checkin_count,
            COUNT(DISTINCT pae.id) AS distinct_checkin_event_count,
            COUNT(DISTINCT pae.power_asset_id) AS unique_checkin_asset_count,
            COUNT(DISTINCT pae.player_id) AS unique_checkin_user_count
          FROM power_asset_event pae
          JOIN params p
            ON p.game_space_id = pae.game_space_id
          WHERE pae.type = 'CHECK_IN'
            AND pae.occurred_datetime >= p.start_datetime_utc
            AND pae.occurred_datetime < p.end_datetime_utc
        )
        SELECT
          c.checkin_count,
          c.distinct_checkin_event_count,
          c.unique_checkin_asset_count,
          c.unique_checkin_user_count,
          r.registered_asset_count,
          ROUND(
            c.unique_checkin_asset_count / NULLIF(r.registered_asset_count, 0) * 100,
            4
          ) AS unique_checkin_asset_rate_percent
        FROM checkin_summary c
        CROSS JOIN registered_assets r;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const rows = yield call(
      getDataFromSxi,
      sqlQuery,
      ENDPOINT.GET_DATA_SXI_DENCHU,
      jwtToken
    );
    const summary = rows?.[0];
    if (!summary) {
      yield put(setHomePictreeCheckInOverviewCommon({
        status: "empty",
        error: null,
        checkinCount: undefined,
        uniqueCheckinAssetCount: undefined,
        uniqueCheckinUserCount: undefined,
        registeredAssetCount: undefined,
        achievementRate: undefined,
      }));
      return;
    }

    yield put(setHomePictreeCheckInOverviewCommon({
      status: "success",
      error: null,
      checkinCount: Number(summary.checkin_count) || 0,
      uniqueCheckinAssetCount: Number(summary.unique_checkin_asset_count) || 0,
      uniqueCheckinUserCount: Number(summary.unique_checkin_user_count) || 0,
      registeredAssetCount: Number(summary.registered_asset_count) || 0,
      achievementRate: Number(summary.unique_checkin_asset_rate_percent) || 0,
    }));
  } catch (err) {
    console.log("[getPictreeDataHomeCheckInOverviewCommon] ERROR", err);
    yield put(setHomePictreeCheckInOverviewCommon({
      status: "error",
      error: err?.message || "Check-in data could not be loaded.",
      checkinCount: undefined,
      uniqueCheckinAssetCount: undefined,
      uniqueCheckinUserCount: undefined,
      registeredAssetCount: undefined,
      achievementRate: undefined,
    }));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataCheckInTrendCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const chartSize = action?.payload?.chartSize || "day";
    const selectedSeasonData = yield select(selectedSeasonCommon);

    if (!Array.isArray(rangeTarget) || rangeTarget.length !== 2 || !selectedSeasonData?.length) {
      yield put(setPictreeDataCheckInTrendCommon([]));
      return;
    }

    const seasonIdList = selectedSeasonData.map(season => `'${season.seasonId}'`).join(", ");
    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, selectedSeasonData);
    const periodExpression = chartSize === "hour"
      ? "DATE_FORMAT(CONVERT_TZ(pae.occurred_datetime, 'UTC', 'Asia/Tokyo'), '%Y-%m-%d %H:00:00')"
      : "DATE(CONVERT_TZ(pae.occurred_datetime, 'UTC', 'Asia/Tokyo'))";

    const rows = yield call(
      getDataFromSxi,
      {
        query: `
          SELECT
            ${periodExpression} AS period,
            COUNT(*) AS checkin_count,
            COUNT(DISTINCT pae.power_asset_id) AS checkin_asset_count,
            COUNT(DISTINCT pae.player_id) AS player_count
          FROM power_asset_event pae
          WHERE pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime < '${endUtc}'
            AND pae.type = 'CHECK_IN'
            AND pae.game_space_id IN (${seasonIdList})
          GROUP BY ${periodExpression}
          ORDER BY period ASC;
        `,
        database: "KpiDashboard",
        type: "list",
      },
      ENDPOINT.GET_DATA_SXI_DENCHU,
      jwtToken
    );

    yield put(setPictreeDataCheckInTrendCommon(rows?.length ? [{
      date: rows.map(row => row.period),
      count: rows.map(row => Number(row.checkin_count) || 0),
      asset: rows.map(row => Number(row.checkin_asset_count) || 0),
      unique: rows.map(row => Number(row.player_count) || 0),
    }] : []));
  } catch (err) {
    console.log("[getPictreeDataCheckInTrendCommon] ERROR", err);
    yield put(setPictreeDataCheckInTrendCommon([]));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataPhotoShootByAssetCommon(action) {
  try {
    yield put(setPageTopLoader(true));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const seasonSelectedCommon = yield select(selectedSeasonCommon);
    const seasonId = seasonSelectedCommon?.[0]?.seasonId || null;
    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, seasonSelectedCommon);

    if (seasonId === "cmt8v3v940001a4zc1b2v7nvx") {
      const query = {
        query: `
          SELECT
            pa.type,
            pa.sub_type,
            COALESCE(patn.name, CONCAT(pa.type, ' / ', pa.sub_type)) AS asset_type_name_ja,
            COUNT(DISTINCT r.power_asset_id) AS review_ok_asset_count
          FROM report r
          JOIN power_asset pa ON pa.id = r.power_asset_id
          LEFT JOIN power_asset_type_name patn
            ON patn.power_asset_type = pa.type
           AND patn.power_asset_sub_type = pa.sub_type
           AND patn.language_code = 'JA'
          WHERE r.game_space_id = '${seasonId}'
            AND r.status = 'APPROVED'
            AND r.report_datetime >= '${startUtc}'
            AND r.report_datetime < '${endUtc}'
          GROUP BY pa.type, pa.sub_type, patn.name
          ORDER BY review_ok_asset_count DESC;
        `,
        database: "KpiDashboard",
        type: "list",
      };
      const rows = yield call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      );
      const payload = (rows || []).map((row) => ({
        type: row.type,
        subType: row.sub_type,
        label: row.asset_type_name_ja || `${row.type} / ${row.sub_type}`,
        CNT: Number(row.review_ok_asset_count) || 0,
      }));
      yield put(setPictreePhotoShootByAssetCommon(payload));
      return;
    }
    const needPadNtt = ["cmb68qvei00011vo0e591229z", "cmr0bus0w00010azches0f7ak"].includes(seasonId);

    // プレースホルダ
    yield put(setPictreePhotoShootByAssetCommon([{ subType: "loading", CNT: 0, label: "-" }]));

    const singleSql = createApprovedBySubtypeQuery({ startUtc, endUtc, seasonId, needPadNtt });

    const sxiEffects = [
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [singleSql, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ];

    const [
      rows,
    ] = yield call(runEffectsInBatches, sxiEffects, { concurrency: 2, gapMs: 300 });

    // ---- 整形：type優先順 + 同type内は通常先 + 件数降順 ----
    const labelMap = chartConfig.chartLabelPicTreeAssetTypeNtt || assetConfig.assetSubTypeNtt;
    const typeRank = { POWER_POLE: 1, MAN_HOLE: 2, HAND_HOLE: 3 };
    const isLucky = (subType) => (subType || "").includes("LUCKY");
    const getLabel = (type, subType) => {
      if (type === "MAN_HOLE" && subType === "NORMAL") return "マンホール";
      if (type === "HAND_HOLE" && subType === "NORMAL") return "ハンドホール";
      return labelMap[subType] || subType || `${type}`;
    };

    const payload = (rows || [])
      .map(({ type, sub_type, reviewOkcnt }) => ({
        type: type || "UNKNOWN",
        subType: sub_type,
        CNT: Number(reviewOkcnt) || 0,
        label: getLabel(type, sub_type),
      }))
      .filter((item) => item.CNT > 0)
      .sort((a, b) => {
        const rankDiff = (typeRank[a.type] || 99) - (typeRank[b.type] || 99);
        if (rankDiff !== 0) return rankDiff;
        const luckyDiff = Number(isLucky(a.subType)) - Number(isLucky(b.subType));
        if (luckyDiff !== 0) return luckyDiff;
        return b.CNT - a.CNT;
      })
      .map(({ type, subType, CNT, label }) => ({ type, subType, CNT, label }));
    yield put(setPictreePhotoShootByAssetCommon(payload));
    } catch (err) {
      console.log("[getPictreeDataPhotoShootByAssetCommon] ERROR", err);
    } finally {
      yield put(setPageTopLoader(false));
    }
}

export function* getPictreeDataByAreaCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const seasonSelected = yield select(selectedSeasonCommon);
    const seasonId = seasonSelected?.[0]?.seasonId || REPORT_SEASON_ID;
    const areaLayerId = action?.payload?.areaLayerId || "japan_1_l1";

    if (!Array.isArray(rangeTarget) || rangeTarget.length < 2 || !rangeTarget[0] || !rangeTarget[1]) {
      yield put(setByAreaDataSummaryCommon(null));
      yield put(setByAreaDataTableCommon([]));
      return;
    }

    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, seasonSelected);

    const query = {
      query: `
        WITH area_map_raw AS (
          SELECT
            paga.power_asset_id,
            paga.area_id,
            MIN(gfad.name) AS area_name,
            MIN(gfa.area_code) AS area_code,
            MIN(
              CASE
                WHEN '${areaLayerId}' = 'japan_1_l2' THEN parent_name.name
                WHEN '${areaLayerId}' = 'japan_1_l3' THEN grandparent_name.name
                ELSE NULL
              END
            ) AS prefecture_name
          FROM game_space gs
          JOIN game_field_layer_bind gflb
            ON gflb.game_field_id = gs.game_field_id
           AND gflb.game_field_layer_id = '${areaLayerId}'
          JOIN game_field_area_bind gfab
            ON gfab.game_field_layer_id = gflb.game_field_layer_id
          JOIN power_asset_game_space_area paga
            ON paga.area_id = gfab.game_field_area_id
           AND paga.game_space_id = gs.id
          LEFT JOIN game_field_area gfa
            ON gfa.id = paga.area_id
          LEFT JOIN game_field_area_display_name gfad
            ON gfad.area_id = gfa.id
           AND gfad.language_code = 'JA'
          LEFT JOIN game_field_area parent_area
            ON parent_area.id = gfa.superior_area_id
          LEFT JOIN game_field_area_display_name parent_name
            ON parent_name.area_id = parent_area.id
           AND parent_name.language_code = 'JA'
          LEFT JOIN game_field_area grandparent_area
            ON grandparent_area.id = parent_area.superior_area_id
          LEFT JOIN game_field_area_display_name grandparent_name
            ON grandparent_name.area_id = grandparent_area.id
           AND grandparent_name.language_code = 'JA'
          WHERE gs.id = '${seasonId}'
          GROUP BY
            paga.power_asset_id,
            paga.area_id
        ),
        area_map AS (
          SELECT
            power_asset_id,
            MIN(COALESCE(area_name, area_code, area_id)) AS area_name,
            MIN(prefecture_name) AS prefecture_name
          FROM area_map_raw
          GROUP BY power_asset_id
        ),
        approved_reports AS (
          SELECT
            r.power_asset_id,
            r.player_id
          FROM report r
          WHERE r.game_space_id = '${seasonId}'
            AND r.status = 'APPROVED'
            AND r.report_datetime >= '${startUtc}'
            AND r.report_datetime < '${endUtc}'
          GROUP BY
            r.power_asset_id,
            r.player_id
        ),
        per_area_registered AS (
          SELECT
            area_name,
            MIN(prefecture_name) AS prefecture_name,
            COUNT(DISTINCT power_asset_id) AS registered_count
          FROM area_map
          GROUP BY area_name
        ),
        per_area_reviewok AS (
          SELECT am.area_name, COUNT(DISTINCT ar.power_asset_id) AS reviewok_count
          FROM approved_reports ar
          JOIN area_map am ON am.power_asset_id = ar.power_asset_id
          GROUP BY am.area_name
        ),
        per_area_participants AS (
          SELECT am.area_name, COUNT(DISTINCT ar.player_id) AS participants_count
          FROM approved_reports ar
          JOIN area_map am ON am.power_asset_id = ar.power_asset_id
          GROUP BY am.area_name
        ),
        global_participants AS (
          SELECT COUNT(DISTINCT player_id) AS total_participants
          FROM approved_reports
        )
        SELECT
          r.area_name AS name,
          r.prefecture_name,
          r.registered_count AS count,
          r.registered_count - COALESCE(o.reviewok_count, 0) AS unshot_count,
          COALESCE(o.reviewok_count, 0) AS reviewok_count,
          ROUND(
            COALESCE(o.reviewok_count, 0) / NULLIF(r.registered_count, 0) * 100,
            2
          ) AS achieved_percent,
          COALESCE(p.participants_count, 0) AS participants_count,
          gp.total_participants AS total_participants
        FROM per_area_registered r
        LEFT JOIN per_area_reviewok o ON o.area_name = r.area_name
        LEFT JOIN per_area_participants p ON p.area_name = r.area_name
        CROSS JOIN global_participants gp
        ORDER BY r.registered_count DESC, r.area_name ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const rows = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );
    const normalizedRows = (rows || []).map((row) => ({
      name: row.name,
      prefectureName: row.prefecture_name || "",
      count: Number(row.count) || 0,
      unshotCount: Number(row.unshot_count) || 0,
      reviewOk: Number(row.reviewok_count) || 0,
      achievedPercent: Number(row.achieved_percent) || 0,
      participants: Number(row.participants_count) || 0,
    }));
    const totals = normalizedRows.reduce((result, row) => ({
      registeredAreaCount: result.registeredAreaCount + 1,
      unshotAreaCount: result.unshotAreaCount + (row.reviewOk === 0 ? 1 : 0),
      totalRegistered: result.totalRegistered + row.count,
      totalReviewOk: result.totalReviewOk + row.reviewOk,
      totalParticipants: result.totalParticipants,
    }), {
      registeredAreaCount: 0,
      unshotAreaCount: 0,
      totalRegistered: 0,
      totalReviewOk: 0,
      totalParticipants: 0,
    });
    totals.totalAchievedPercent = totals.totalRegistered
      ? Number(((totals.totalReviewOk / totals.totalRegistered) * 100).toFixed(2))
      : 0;
    totals.totalParticipants = Number(rows?.[0]?.total_participants) || 0;

    const table = normalizedRows.map((row, index) => [
      index + 1,
      row.name,
      ...(areaLayerId !== "japan_1_l1" ? [row.prefectureName] : []),
      row.count,
      row.unshotCount,
      row.reviewOk,
      row.achievedPercent,
      row.participants,
    ]);
    yield put(setByAreaDataCommon({ summary: totals, table }));
  } catch (err) {
    console.log("[getPictreeDataByAreaCommon] ERROR", err);
    yield put(setByAreaDataSummaryCommon(null));
    yield put(setByAreaDataTableCommon([]));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataByAreaCheckInCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const seasonSelected = yield select(selectedSeasonCommon);
    const seasonId = seasonSelected?.[0]?.seasonId || null;
    const areaLayerId = action?.payload?.areaLayerId || "japan_1_l1";

    if (!seasonId || !Array.isArray(rangeTarget) || rangeTarget.length < 2 || !rangeTarget[0] || !rangeTarget[1]) {
      yield put(setByAreaCheckInDataCommon({ status: "empty", rows: [], summary: null }));
      return;
    }

    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, seasonSelected);
    const query = {
      query: `
        WITH params AS (
          SELECT
            '${seasonId}' AS game_space_id,
            '${startUtc}' AS start_datetime_utc,
            '${endUtc}' AS end_datetime_utc,
            '${areaLayerId}' AS area_layer_id
        )
        SELECT
          gfa.area_code,
          COALESCE(gfad.name, gfa.area_code, CAST(gfa.id AS CHAR)) AS area_name,
          CASE
            WHEN p.area_layer_id = 'japan_1_l2' THEN parent_name.name
            WHEN p.area_layer_id = 'japan_1_l3' THEN grandparent_name.name
            ELSE NULL
          END AS prefecture_name,
          COUNT(DISTINCT paga.power_asset_id) AS registered_asset_count,
          COUNT(DISTINCT pae.id) AS checkin_count,
          COUNT(DISTINCT pae.power_asset_id) AS checkin_asset_count,
          COUNT(DISTINCT pae.player_id) AS checkin_user_count,
          ROUND(
            COUNT(DISTINCT pae.power_asset_id)
              / NULLIF(COUNT(DISTINCT paga.power_asset_id), 0) * 100,
            2
          ) AS achievement_rate_percent,
          CASE WHEN COUNT(DISTINCT pae.id) = 0 THEN 1 ELSE 0 END AS unchecked_area_flag
        FROM game_space gs
        JOIN params p ON p.game_space_id = gs.id
        JOIN game_field_layer_bind gflb
          ON gflb.game_field_id = gs.game_field_id
         AND gflb.game_field_layer_id = p.area_layer_id
        JOIN game_field_area_bind gfab
          ON gfab.game_field_layer_id = gflb.game_field_layer_id
        JOIN power_asset_game_space_area paga
          ON paga.area_id = gfab.game_field_area_id
         AND paga.game_space_id = gs.id
        LEFT JOIN game_field_area gfa
          ON gfa.id = paga.area_id
        LEFT JOIN game_field_area_display_name gfad
          ON gfad.area_id = gfa.id
         AND gfad.language_code = 'JA'
        LEFT JOIN game_field_area parent_area
          ON parent_area.id = gfa.superior_area_id
        LEFT JOIN game_field_area_display_name parent_name
          ON parent_name.area_id = parent_area.id
         AND parent_name.language_code = 'JA'
        LEFT JOIN game_field_area grandparent_area
          ON grandparent_area.id = parent_area.superior_area_id
        LEFT JOIN game_field_area_display_name grandparent_name
          ON grandparent_name.area_id = grandparent_area.id
         AND grandparent_name.language_code = 'JA'
        LEFT JOIN power_asset_event pae
          ON pae.game_space_id = p.game_space_id
         AND pae.power_asset_id = paga.power_asset_id
         AND pae.type = 'CHECK_IN'
         AND pae.occurred_datetime >= p.start_datetime_utc
         AND pae.occurred_datetime < p.end_datetime_utc
        WHERE gs.id = p.game_space_id
        GROUP BY
          gfa.area_code,
          COALESCE(gfad.name, gfa.area_code, CAST(gfa.id AS CHAR)),
          gfa.id,
          CASE
            WHEN p.area_layer_id = 'japan_1_l2' THEN parent_name.name
            WHEN p.area_layer_id = 'japan_1_l3' THEN grandparent_name.name
            ELSE NULL
          END
        ORDER BY COUNT(DISTINCT pae.id) DESC, gfa.area_code ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };
    const result = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );
    const rows = (result || []).map((row, index) => ({
      index: index + 1,
      areaCode: row.area_code,
      areaName: row.area_name,
      prefectureName: row.prefecture_name || "",
      registeredAssetCount: Number(row.registered_asset_count) || 0,
      checkinCount: Number(row.checkin_count) || 0,
      checkinAssetCount: Number(row.checkin_asset_count) || 0,
      checkinUserCount: Number(row.checkin_user_count) || 0,
      achievementRatePercent: Number(row.achievement_rate_percent) || 0,
      uncheckedAreaFlag: Number(row.unchecked_area_flag) || 0,
    }));
    const summary = rows.reduce((total, row) => ({
      registeredAreaCount: total.registeredAreaCount + 1,
      uncheckedAreaCount: total.uncheckedAreaCount + row.uncheckedAreaFlag,
      totalRegistered: total.totalRegistered + row.registeredAssetCount,
      totalCheckin: total.totalCheckin + row.checkinCount,
      totalCheckinAssets: total.totalCheckinAssets + row.checkinAssetCount,
      totalAreaUsers: total.totalAreaUsers + row.checkinUserCount,
    }), {
      registeredAreaCount: 0,
      uncheckedAreaCount: 0,
      totalRegistered: 0,
      totalCheckin: 0,
      totalCheckinAssets: 0,
      totalAreaUsers: 0,
    });
    summary.totalAchievementRate = summary.totalRegistered
      ? Number(((summary.totalCheckinAssets / summary.totalRegistered) * 100).toFixed(2))
      : 0;
    yield put(setByAreaCheckInDataCommon({
      status: rows.length > 0 ? "success" : "empty",
      error: null,
      rows,
      summary,
    }));
  } catch (err) {
    console.log("[getPictreeDataByAreaCheckInCommon] ERROR", err);
    yield put(setByAreaCheckInDataCommon({ status: "error", error: err.message || "取得に失敗しました", rows: [], summary: null }));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataPhotoShootTrendCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const seasonSelected = yield select(selectedSeasonCommon);
    const seasonIds = seasonSelected.map((season) => `'${season.seasonId}'`).join(", ");
    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, seasonSelected);
    const chartSize = typeof action.payload === "string" ? action.payload : action.payload?.chartSize;
    const isHour = chartSize === "hour";
    const periodExpression = isHour
      ? "DATE_FORMAT(CONVERT_TZ(r.report_datetime, 'UTC', 'Asia/Tokyo'), '%Y-%m-%d %H:00')"
      : "DATE(CONVERT_TZ(r.report_datetime, 'UTC', 'Asia/Tokyo'))";

    const query = {
      query: `
        SELECT
          ${periodExpression} AS period,
          COUNT(DISTINCT CASE WHEN rp.id IS NOT NULL THEN r.id END) AS report_with_photo_count,
          COUNT(DISTINCT CASE WHEN rp.id IS NULL THEN r.id END) AS report_without_photo_count,
          COUNT(DISTINCT CASE WHEN r.status = 'APPROVED' THEN r.power_asset_id END) AS review_ok_asset_count,
          COUNT(DISTINCT CASE WHEN r.status = 'APPROVED' THEN r.player_id END) AS unique_photographers
        FROM report r
        LEFT JOIN report_photograph_bind rpb
          ON rpb.report_id = r.id
        LEFT JOIN report_photograph rp
          ON rp.id = rpb.report_photograph_id
        WHERE r.game_space_id IN (${seasonIds})
          AND r.report_datetime >= '${startUtc}'
          AND r.report_datetime <  '${endUtc}'
        GROUP BY period
        ORDER BY period ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const rows = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );
    const periods = (rows || []).map((row) =>
      moment.utc(row.period).format(isHour ? "YYYY-MM-DD HH:00" : "YYYY-MM-DD")
    );
    const toNumberArray = (key) => (rows || []).map((row) => Number(row[key]) || 0);
    const trend = periods.length > 0 ? [{
      date: periods,
      countReportWithPhoto: toNumberArray("report_with_photo_count"),
      countReportWithoutPhoto: toNumberArray("report_without_photo_count"),
      countReviewOk: toNumberArray("review_ok_asset_count"),
      unique: toNumberArray("unique_photographers"),
    }] : [];

    yield put(setPictreeDataPhotoShootTrendCommon(trend));
  } catch (err) {
    console.log("[getPictreeDataPhotoShootTrendCommon] ERROR", err);
    yield put(setPictreeDataPhotoShootTrendCommon([]));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataPhotoShootTrendByAssetCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const seasonSelected = yield select(selectedSeasonCommon);
    const seasonId = seasonSelected?.[0]?.seasonId || null;
    const chartSize = typeof action.payload === "string" ? action.payload : action.payload?.chartSize;
    const isHour = chartSize === "hour";
    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, seasonSelected);
    const periodExpression = isHour
      ? "DATE_FORMAT(CONVERT_TZ(r.report_datetime, 'UTC', 'Asia/Tokyo'), '%Y-%m-%d %H:00')"
      : "DATE(CONVERT_TZ(r.report_datetime, 'UTC', 'Asia/Tokyo'))";
    const query = {
      query: `
        SELECT
          ${periodExpression} AS period,
          pa.type,
          pa.sub_type,
          COALESCE(patn.name, CONCAT(pa.type, ' / ', pa.sub_type)) AS asset_type_name_ja,
          COUNT(DISTINCT r.power_asset_id) AS review_ok_asset_count
        FROM report r
        JOIN power_asset pa ON pa.id = r.power_asset_id
        LEFT JOIN power_asset_type_name patn
          ON patn.power_asset_type = pa.type
         AND patn.power_asset_sub_type = pa.sub_type
         AND patn.language_code = 'JA'
        WHERE r.game_space_id IN ('${seasonId}')
          AND r.status = 'APPROVED'
          AND r.report_datetime >= '${startUtc}'
          AND r.report_datetime < '${endUtc}'
        GROUP BY period, pa.type, pa.sub_type, patn.name
        ORDER BY period ASC, review_ok_asset_count DESC;
      `,
      database: "KpiDashboard",
      type: "list",
    };
    const rows = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );
    const periods = [...new Set((rows || []).map((row) =>
      moment.utc(row.period).format(isHour ? "YYYY-MM-DD HH:00" : "YYYY-MM-DD")
    ))];
    const labels = [...new Set((rows || []).map((row) =>
      row.asset_type_name_ja || `${row.type} / ${row.sub_type}`
    ))];
    const trend = periods.length > 0 ? [{ date: periods }] : [];
    labels.forEach((label) => {
      if (trend.length === 0) return;
      trend[0][label] = periods.map((period) => {
        const row = (rows || []).find((item) => {
          const itemPeriod = moment.utc(item.period).format(isHour ? "YYYY-MM-DD HH:00" : "YYYY-MM-DD");
          const itemLabel = item.asset_type_name_ja || `${item.type} / ${item.sub_type}`;
          return itemPeriod === period && itemLabel === label;
        });
        return Number(row?.review_ok_asset_count) || 0;
      });
    });
    yield put(setPictreePhotoShootTrendByAssetCommon(trend));
  } catch (err) {
    console.log("[getPictreeDataPhotoShootTrendByAssetCommon] ERROR", err);
    yield put(setPictreePhotoShootTrendByAssetCommon([]));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataCheckInByAssetCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const seasonSelected = yield select(selectedSeasonCommon);
    const seasonId = seasonSelected?.[0]?.seasonId || null;
    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, seasonSelected);

    if (!seasonId) {
      yield put(setPictreeCheckInByAssetCommon({ status: "empty", breakdown: [] }));
      return;
    }

    const query = {
      query: `
        SELECT
          pa.type,
          pa.sub_type,
          COALESCE(patn.name, CONCAT(pa.type, ' / ', pa.sub_type)) AS asset_type_name_ja,
          COUNT(*) AS checkin_event_count,
          COUNT(DISTINCT pae.id) AS distinct_checkin_event_count,
          COUNT(DISTINCT pae.power_asset_id) AS unique_checkin_asset_count,
          COUNT(DISTINCT pae.player_id) AS unique_checkin_user_count
        FROM power_asset_event pae
        JOIN power_asset pa ON pa.id = pae.power_asset_id
        LEFT JOIN power_asset_type_name patn
          ON patn.power_asset_type = pa.type
         AND patn.power_asset_sub_type = pa.sub_type
         AND patn.language_code = 'JA'
        WHERE pae.game_space_id = '${seasonId}'
          AND pae.type = 'CHECK_IN'
          AND pae.occurred_datetime >= '${startUtc}'
          AND pae.occurred_datetime < '${endUtc}'
        GROUP BY pa.type, pa.sub_type, patn.name
        ORDER BY checkin_event_count DESC, pa.type ASC, pa.sub_type ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };
    const rows = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );
    const breakdown = (rows || []).map((row) => ({
      type: row.type,
      subType: row.sub_type,
      label: row.asset_type_name_ja || `${row.type} / ${row.sub_type}`,
      checkinCount: Number(row.checkin_event_count) || 0,
      distinctCheckinEventCount: Number(row.distinct_checkin_event_count) || 0,
      uniqueCheckinAssetCount: Number(row.unique_checkin_asset_count) || 0,
      uniqueCheckinUserCount: Number(row.unique_checkin_user_count) || 0,
    }));
    yield put(setPictreeCheckInByAssetCommon({
      status: breakdown.length > 0 ? "success" : "empty",
      breakdown,
    }));
  } catch (err) {
    console.log("[getPictreeDataCheckInByAssetCommon] ERROR", err);
    yield put(setPictreeCheckInByAssetCommon({ status: "error", error: err.message || "取得に失敗しました", breakdown: [] }));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataCheckInTrendByAssetCommon(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = action?.payload?.dateRange || (yield select(dateRange));
    const seasonSelected = yield select(selectedSeasonCommon);
    const seasonId = seasonSelected?.[0]?.seasonId || null;
    const { startUtc, endUtc } = getCommonDataRangeUtc(rangeTarget, seasonSelected);

    if (!seasonId) {
      yield put(setPictreeCheckInTrendByAssetCommon({ status: "empty", trend: [] }));
      return;
    }

    const periodExpression = "DATE(CONVERT_TZ(pae.occurred_datetime, 'UTC', 'Asia/Tokyo'))";
    const query = {
      query: `
        SELECT
          ${periodExpression} AS period_jst,
          pa.type,
          pa.sub_type,
          COALESCE(patn.name, CONCAT(pa.type, ' / ', pa.sub_type)) AS asset_type_name_ja,
          COUNT(*) AS checkin_event_count,
          COUNT(DISTINCT pae.id) AS distinct_checkin_event_count,
          COUNT(DISTINCT pae.power_asset_id) AS unique_checkin_asset_count,
          COUNT(DISTINCT pae.player_id) AS unique_checkin_user_count
        FROM power_asset_event pae
        JOIN power_asset pa ON pa.id = pae.power_asset_id
        LEFT JOIN power_asset_type_name patn
          ON patn.power_asset_type = pa.type
         AND patn.power_asset_sub_type = pa.sub_type
         AND patn.language_code = 'JA'
        WHERE pae.game_space_id = '${seasonId}'
          AND pae.type = 'CHECK_IN'
          AND pae.occurred_datetime >= '${startUtc}'
          AND pae.occurred_datetime < '${endUtc}'
        GROUP BY period_jst, pa.type, pa.sub_type, patn.name
        ORDER BY period_jst ASC, checkin_event_count DESC, pa.sub_type ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };
    const rows = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );
    const dates = [...new Set((rows || []).map((row) => moment.utc(row.period_jst).format("YYYY-MM-DD")))];
    const labels = [...new Set((rows || []).map((row) => row.asset_type_name_ja || `${row.type} / ${row.sub_type}`))];
    const trend = dates.length > 0 ? [{ date: dates }] : [];
    labels.forEach((label) => {
      trend[0][label] = dates.map((date) => {
        const row = (rows || []).find((item) => (
          moment.utc(item.period_jst).format("YYYY-MM-DD") === date
          && (item.asset_type_name_ja || `${item.type} / ${item.sub_type}`) === label
        ));
        return Number(row?.checkin_event_count) || 0;
      });
    });
    yield put(setPictreeCheckInTrendByAssetCommon({
      status: trend.length > 0 ? "success" : "empty",
      trend,
    }));
  } catch (err) {
    console.log("[getPictreeDataCheckInTrendByAssetCommon] ERROR", err);
    yield put(setPictreeCheckInTrendByAssetCommon({ status: "error", error: err.message || "取得に失敗しました", trend: [] }));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export default function* watchPageSettings() {
  yield takeEvery(PICTREE.GET_HOME_PHOTOSHOOT_COMMON, getPictreeDataHomePhotoShootCommon);
  yield takeEvery(PICTREE.GET_PHOTOSHOOT_TREND_COMMON, getPictreeDataPhotoShootTrendCommon);
  yield takeEvery(PICTREE.GET_PHOTOSHOOT_TREND_BY_ASSET_COMMON, getPictreeDataPhotoShootTrendByAssetCommon);
  yield takeEvery(PICTREE.GET_PHOTOSHOOT_TREND_COMMON, getPictreeDataHomePhotoShootTrendCommon);
  yield takeEvery(PICTREE.GET_HOME_CHECKIN_COMMON, getPictreeDataHomeCheckInCommon);
  yield takeEvery(PICTREE.GET_HOME_CHECKIN_OVERVIEW_COMMON, getPictreeDataHomeCheckInOverviewCommon);
  yield takeEvery(PICTREE.GET_CHECKIN_TREND_COMMON, getPictreeDataCheckInTrendCommon);
  yield takeEvery(PICTREE.GET_PHOTOSHOOT_BY_ASSET_COMMON, getPictreeDataPhotoShootByAssetCommon);
  yield takeEvery(PICTREE.GET_CHECKIN_BY_ASSET_COMMON, getPictreeDataCheckInByAssetCommon);
  yield takeEvery(PICTREE.GET_CHECKIN_TREND_BY_ASSET_COMMON, getPictreeDataCheckInTrendByAssetCommon);
  yield takeEvery(PICTREE.GET_BY_AREA_DATA_COMMON, getPictreeDataByAreaCommon);
  yield takeEvery(PICTREE.GET_BY_AREA_CHECKIN_DATA_COMMON, getPictreeDataByAreaCheckInCommon);
}
